import { defineStore } from 'pinia';
import { print } from 'graphql';
import Echo from '@/echo';
import { useSetupStore } from '@/stores/setup';
import PresenceApi from '@/store/api/presence';
import Auth from '@/store/api/system/auth';
import SetPresenceGql from '@/store/graphql/presence/mutations/SetPresence.gql';

// A stable per-TAB client id. The registry is keyed by instance (not by user), so the
// same account open in another tab/computer shows up as a distinct viewer — a shared
// shop login can see "also open on the other till". sessionStorage scopes it to this
// tab and survives a reload (a reload is the same instance, not a new one).
const resolveInstanceId = () => {
    const random = () => `inst-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    try {
        let id = sessionStorage.getItem('presenceInstanceId');
        if (!id) {
            id = random();
            sessionStorage.setItem('presenceInstanceId', id);
        }
        return id;
    } catch (error) {
        return random();
    }
};

// A presence "room": the registry is partitioned by it, and every client in the same
// room sees the same snapshot. Normally the active CMS — but a scope can name its own
// (the platform ticket desk is room 0: our own work belongs to no single tenant).
// Three outcomes, and they are NOT the same:
//   null      → the platform desk, explicitly (our own work, no CMS)
//   a number  → that CMS's room
//   undefined → nothing to subscribe to (no room given and no active CMS)
const resolveRoom = room => {
    if (room === null) return null;
    if (room !== undefined && room !== '') return parseInt(room, 10);

    const cms = useSetupStore().activeCms;
    return cms && cms.id ? parseInt(cms.id, 10) : undefined;
};

// NULL has no `cms.{id}` channel to ride — it is the platform desk, mirrored by the
// backend's PresenceUpdated::broadcastOn().
const roomChannel = room => (room === null ? 'presence.platform' : `cms.${room}`);

// Generic, system-wide presence store. State is keyed by scope so any feature can
// share it: byScope[scope] = [{ instance_id, user_id, name, context, since }]. Seeded
// by load(), kept live by the per-scope `presence:{scope}` broadcast on the room channel.
export const usePresenceStore = defineStore('presence', {
    state: () => ({
        byScope: {},
        // scope -> cmsId currently subscribed (so subscribe() is idempotent)
        subscribed: {},
        // This tab's presence instance id (keys our own entry in the registry).
        instanceId: resolveInstanceId()
    }),
    actions: {
        setSnapshot(scope, entries) {
            this.byScope = { ...this.byScope, [scope]: entries || [] };
        },

        load(scope, room) {
            const id = resolveRoom(room);
            if (id === undefined) return Promise.resolve();
            return PresenceApi.fetchPresence(scope, id)
                .then(entries => this.setSnapshot(scope, entries))
                .catch(() => {});
        },

        // Announce/refresh (context set) or clear (context null) this tab's presence.
        announce(scope, context, room) {
            const id = resolveRoom(room);
            if (id === undefined) return Promise.resolve();
            return PresenceApi.setPresence(scope, id, context, this.instanceId)
                .then(entries => this.setSnapshot(scope, entries))
                .catch(() => {});
        },

        // Best-effort "I'm leaving" sent during page unload (tab/window close, reload).
        // Vue's beforeDestroy doesn't fire on a real tab close, so without this the
        // closed tab lingers in the registry until its TTL and other clients only drop
        // it on the next heartbeat broadcast (never, if they're idle on the list). A
        // keepalive fetch survives unload AND carries the JWT (sendBeacon can't set the
        // Authorization header), so the server removes us and broadcasts immediately.
        announceLeaveBeacon(scope, room) {
            const id = resolveRoom(room);
            if (id === undefined) return;
            const token = Auth.getToken();
            if (!token) return;

            const endpoint = `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_GRAPHQL_ROUTE}admin`;
            const body = JSON.stringify({
                query: print(SetPresenceGql),
                variables: { scope, cms_id: id, context: null, instance_id: this.instanceId }
            });

            try {
                fetch(endpoint, {
                    method: 'POST',
                    keepalive: true,
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body
                }).catch(() => {});
            } catch (error) { /* unload is best-effort; TTL is the backstop */ }
        },

        // Listen for live deltas on the CMS channel. Adds only this scope's listener
        // (stopListening on teardown) so it never disturbs other features sharing the
        // same channel. Idempotent per scope+CMS.
        subscribe(scope, room) {
            const id = resolveRoom(room);
            if (id === undefined) return;
            if (this.subscribed[scope] === id) return;
            if (!Echo.client) Echo.initialize();
            if (!Echo.client) return;

            this.unsubscribe(scope);
            Echo.client.private(roomChannel(id))
                .listen(`.presence:${scope}`, payload => this.setSnapshot(scope, payload && payload.entries));
            this.subscribed = { ...this.subscribed, [scope]: id };
        },

        unsubscribe(scope) {
            const room = this.subscribed[scope];
            if (room !== undefined && Echo.client) {
                try { Echo.client.private(roomChannel(room)).stopListening(`.presence:${scope}`); } catch (e) { /* noop */ }
            }
            const next = { ...this.subscribed };
            delete next[scope];
            this.subscribed = next;
        },

        // Leave every scope this tab is present in, for the CURRENT cms, and drop its
        // listeners. Call this BEFORE switching CMS/user (while the old cms + token are
        // still active) — otherwise our registry entry lingers in the old cms until its
        // TTL and other clients keep showing our "in use" badge. The entry is keyed by
        // our instance id, so this clears exactly this tab.
        leaveAll() {
            Object.keys(this.subscribed).forEach(scope => {
                // Leave the room we are actually IN, which is not necessarily the
                // active CMS — a ticket desk keeps its own.
                this.announce(scope, null, this.subscribed[scope]);
                this.unsubscribe(scope);
            });
        },

        // Everyone present at a given context within a scope EXCEPT this very tab.
        // Filters by instance (not user), so the same account in another tab/device is
        // included — the caller flags those (entry.user_id === me) as "your other device".
        viewersOf(scope, context) {
            return (this.byScope[scope] || []).filter(entry =>
                entry.context === context && entry.instance_id !== this.instanceId
            );
        }
    }
});
